import type { Machine } from '@bridgewerk/shared';
import { Skeleton } from '../../../components/ui/Skeleton.js';

export interface OEEPanelProps {
  readonly machines: Machine[] | undefined;
  readonly loading?: boolean;
}

export function OEEPanel({ machines, loading }: OEEPanelProps): JSX.Element {
  return (
    <section className="rounded-md border border-border bg-card overflow-hidden">
      <header className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
          OEE Analysis — Live
        </h2>
        <span className="text-[10px] font-mono text-accent">WERK·OS</span>
      </header>
      <div className="p-4 space-y-3">
        {loading && !machines ? (
          [0, 1, 2].map((i) => <Skeleton key={i} className="h-7" />)
        ) : !machines || machines.length === 0 ? (
          <EmptyState />
        ) : (
          machines.map((m) => <OEERow key={m.machine_id} machine={m} />)
        )}
      </div>
      <footer
        className="m-4 mt-0 p-3 rounded-md text-[12px] leading-snug"
        style={{
          backgroundColor: 'rgba(0,196,232,0.06)',
          border: '1px solid rgba(0,196,232,0.15)',
        }}
      >
        <span className="text-accent font-semibold">⚡ WerkOS:</span>{' '}
        <span className="text-text-dim">
          OEE insights compound over the first 7 days as the model learns your floor.
        </span>
      </footer>
    </section>
  );
}

function OEERow({ machine }: { readonly machine: Machine }): JSX.Element {
  const pct = Math.max(0, Math.min(100, machine.oee_percent));
  const color = pct >= 70 ? 'var(--color-green)' : pct >= 50 ? 'var(--color-yellow)' : 'var(--color-red)';
  return (
    <div className="grid grid-cols-[120px_1fr_44px] items-center gap-3">
      <span className="font-mono text-xs truncate">{machine.machine_id}</span>
      <div
        className="h-1.5 rounded-full overflow-hidden"
        style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full transition-[width] duration-700 ease-out"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="font-mono text-xs text-right" style={{ color }}>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <p className="text-xs text-text-dim text-center py-6">No telemetry yet.</p>
  );
}
